/**
 * components/TransformrPanel.tsx - Transformr Panel Component
 * 
 * CHANGE HISTORY:
 * - 251103_1520 [pending] danielbeer: Panel component with format selector and display modes
 * 
 * Individual panel for source or target with format selection and display modes.
 */

import React, { useCallback } from 'react';
import type { DataFormat, DisplayMode, PanelConfig } from '../types';
import { SAMPLE_TEXTS } from '../data/sampleTexts';

export interface TranslatrPanelProps {
  /** Panel type */
  type: 'source' | 'target';
  
  /** Panel configuration */
  config: PanelConfig;
  
  /** Available formats */
  availableFormats: DataFormat[];
  
  /** Content change handler */
  onContentChange?: (content: string) => void;
  
  /** Format change handler */
  onFormatChange: (format: DataFormat) => void;
  
  /** Display mode change handler */
  onDisplayModeChange: (mode: DisplayMode) => void;
  
  /** Show format selector */
  showFormatSelector?: boolean;
  
  /** Show display modes */
  showDisplayModes?: boolean;
  
  /** Read only */
  readOnly?: boolean;
  
  /** Theme */
  theme?: 'light' | 'dark';
  
  /** Load sample handler (source panel only) */
  onLoadSample?: (sampleId: string, content: string) => void;
  
  /** Clear handler (source panel only) */
  onClear?: () => void;
}

/**
 * TranslatrPanel - Individual panel for source or target
 */
export const TranslatrPanel: React.FC<TranslatrPanelProps> = ({
  type,
  config,
  availableFormats,
  onContentChange,
  onFormatChange,
  onDisplayModeChange,
  showFormatSelector = true,
  showDisplayModes = true,
  readOnly = false,
  theme = 'light',
  onLoadSample,
  onClear
}) => {
  const [showSampleMenu, setShowSampleMenu] = React.useState(false);
  // Handle content change
  const handleContentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (onContentChange) {
      onContentChange(e.target.value);
    }
  }, [onContentChange]);

  // Handle format change
  const handleFormatChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    onFormatChange(e.target.value as DataFormat);
  }, [onFormatChange]);

  // Handle display mode change
  const handleDisplayModeChange = useCallback((mode: DisplayMode) => {
    onDisplayModeChange(mode);
  }, [onDisplayModeChange]);

  // Get format label
  const getFormatLabel = (format: DataFormat): string => {
    const labels: Record<DataFormat, string> = {
      auto: '🔍 Auto-Detect',
      json: 'JSON',
      csv: 'CSV',
      tsv: 'TSV',
      xml: 'XML',
      yaml: 'YAML',
      markdown: 'Markdown',
      html: 'HTML',
      text: 'Text',
      vcard: 'vCard',
      icalendar: 'iCalendar',
      contact: '👤 Contact Details'
    };
    return labels[format] || format.toUpperCase();
  };

  // Render content based on display mode
  const renderContent = () => {
    const content = config.content;
    
    switch (config.displayMode) {
      case 'raw':
        return (
          <textarea
            className="translatr-panel__textarea"
            value={content}
            onChange={handleContentChange}
            readOnly={readOnly}
            placeholder={type === 'source' ? 'Paste your data here...' : 'Transformed output will appear here...'}
            spellCheck={false}
          />
        );
      
      case 'formatted':
        // For formatted view, try to pretty-print
        let formattedContent = content;
        try {
          if (config.format === 'json' || config.detectedFormat === 'json') {
            const parsed = JSON.parse(content);
            formattedContent = JSON.stringify(parsed, null, 2);
          }
        } catch {
          formattedContent = content;
        }
        
        return (
          <pre className="translatr-panel__pre">
            <code className={`language-${config.format}`}>
              {formattedContent}
            </code>
          </pre>
        );
      
      case 'inspector':
        // For inspector view, show tree structure
        let inspectorData: any = null;
        try {
          if (config.parsedData) {
            inspectorData = config.parsedData;
          } else if (content) {
            inspectorData = JSON.parse(content);
          }
        } catch {
          inspectorData = { error: 'Unable to parse data for inspector view' };
        }
        
        return (
          <div className="translatr-panel__inspector">
            {renderInspectorTree(inspectorData)}
          </div>
        );
      
      default:
        return null;
    }
  };

  // Render inspector tree
  const renderInspectorTree = (data: any, depth: number = 0): React.ReactNode => {
    if (data === null) return <span className="inspector-null">null</span>;
    if (data === undefined) return <span className="inspector-undefined">undefined</span>;
    
    const type = typeof data;
    
    if (type === 'string') {
      return <span className="inspector-string">"{data}"</span>;
    }
    if (type === 'number') {
      return <span className="inspector-number">{data}</span>;
    }
    if (type === 'boolean') {
      return <span className="inspector-boolean">{String(data)}</span>;
    }
    
    if (Array.isArray(data)) {
      if (data.length === 0) return <span className="inspector-array">[]</span>;
      
      return (
        <details className="inspector-details" open={depth < 2}>
          <summary className="inspector-summary">
            Array[{data.length}]
          </summary>
          <div className="inspector-children">
            {data.map((item, idx) => (
              <div key={idx} className="inspector-item">
                <span className="inspector-key">[{idx}]:</span>
                {renderInspectorTree(item, depth + 1)}
              </div>
            ))}
          </div>
        </details>
      );
    }
    
    if (type === 'object') {
      const keys = Object.keys(data);
      if (keys.length === 0) return <span className="inspector-object">{'{}'}</span>;
      
      return (
        <details className="inspector-details" open={depth < 2}>
          <summary className="inspector-summary">
            Object{'{'}...{'}'}
          </summary>
          <div className="inspector-children">
            {keys.map(key => (
              <div key={key} className="inspector-item">
                <span className="inspector-key">{key}:</span>
                {renderInspectorTree(data[key], depth + 1)}
              </div>
            ))}
          </div>
        </details>
      );
    }
    
    return <span>{String(data)}</span>;
  };

  return (
    <div className={`translatr-panel translatr-panel--${type} translatr-panel--${theme}`}>
      {/* Panel header */}
      <div className="translatr-panel__header">
        <h3 className="translatr-panel__title">
          {type === 'source' ? '📥 Source' : '📤 Target'}
        </h3>
        
        {/* Format selector */}
        {showFormatSelector && (
          <div className="translatr-panel__format">
            <label htmlFor={`format-${type}`}>Format:</label>
            <select
              id={`format-${type}`}
              className="translatr-panel__select"
              value={config.format}
              onChange={handleFormatChange}
            >
              {availableFormats.map(format => (
                <option key={format} value={format}>
                  {getFormatLabel(format)}
                </option>
              ))}
            </select>
            
            {/* Show detected format if auto */}
            {config.format === 'auto' && config.detectedFormat && (
              <span className="translatr-panel__detected">
                → {getFormatLabel(config.detectedFormat)}
                {config.confidence && ` (${config.confidence}%)`}
              </span>
            )}
          </div>
        )}
        
        {/* Display mode toggles */}
        {showDisplayModes && (
          <div className="translatr-panel__modes">
            <div className="translatr-panel__modes-left">
              <button
                className={`translatr-panel__mode-btn ${config.displayMode === 'raw' ? 'active' : ''}`}
                onClick={() => handleDisplayModeChange('raw')}
                title="Raw text view"
              >
                📝 Raw
              </button>
              <button
                className={`translatr-panel__mode-btn ${config.displayMode === 'formatted' ? 'active' : ''}`}
                onClick={() => handleDisplayModeChange('formatted')}
                title="Formatted view with syntax highlighting"
              >
                ✨ Formatted
              </button>
              <button
                className={`translatr-panel__mode-btn ${config.displayMode === 'inspector' ? 'active' : ''}`}
                onClick={() => handleDisplayModeChange('inspector')}
                title="Tree inspector view"
              >
                🔍 Inspector
              </button>
            </div>
            
            {/* Sample Text and Clear buttons (source panel only) - aligned right */}
            {type === 'source' && (onLoadSample || onClear) && (
              <div className="translatr-panel__modes-right">
                {onLoadSample && (
                  <div className="translatr-panel__sample-container">
                    <button
                      className="translatr-panel__mode-btn"
                      onClick={() => setShowSampleMenu(!showSampleMenu)}
                      title="Load sample text data"
                    >
                      🅣 Samples...
                    </button>
                    {showSampleMenu && (
                      <div className="translatr-panel__sample-menu">
                        <div className="translatr-panel__sample-header">
                          <h4>Sample Texts</h4>
                          <button onClick={() => setShowSampleMenu(false)} className="translatr-panel__close">✕</button>
                        </div>
                        <div className="translatr-panel__sample-list">
                          {SAMPLE_TEXTS.map(sample => (
                            <button
                              key={sample.id}
                              onClick={() => {
                                onLoadSample?.(sample.id, sample.content);
                                setShowSampleMenu(false);
                              }}
                              className="translatr-panel__sample-item"
                            >
                              <div className="translatr-panel__sample-name">{sample.name}</div>
                              <div className="translatr-panel__sample-desc">{sample.description}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {onClear && (
                  <button
                    className="translatr-panel__mode-btn"
                    onClick={onClear}
                    title="Clear all content"
                  >
                    🗑️
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Panel content */}
      <div className="translatr-panel__content">
        {renderContent()}
      </div>
      
      {/* Panel footer with stats */}
      <div className="translatr-panel__stats">
        <span className="translatr-panel__stat">
          {(config.content || '').length} characters
        </span>
        <span className="translatr-panel__stat">
          {(config.content || '').split('\n').length} lines
        </span>
        {config.parsedData && (
          <span className="translatr-panel__stat">
            {Array.isArray(config.parsedData) ? `${config.parsedData.length} records` : '1 record'}
          </span>
        )}
      </div>
    </div>
  );
};
